import React, {useEffect, useState} from "react";
import moment from "moment/moment";
import {Battery} from "@pxblue/react-progress-icons";
import CurrentSymbol from "./CurrentSymbol";
import TestResultSymbol from "./TestResultSymbol";
import InstallService from "../../../services/install.service";
import {Green, Grey, Red, Yellow} from "../InstallationCheck";
import DevTemp from "../DevTemp";
import WiFiSymbol from "../WiFiSymbol";
import SignalSymbol from "../SignalSymbol";
import RequestPayloadModal from "../../InstallReport/RequestPayloadModal";
import {ModalView} from "../../GoogleAnalytics";
import MobileInstallCheckService from "../../../services/mobileInstallCheck.service";
import DeviceService from "../../../services/device.service";
import WarningModal from "../../Tools/WarningModal";

export const Failure = 'Failure';
export const Warning = 'Warning';
export const Passed = 'Passed';

function HistoricalInstallCheck({ roles, installId, registeredDevices, requestPayload, macId }) {

    const [isMounted, setIsMounted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showNoDevices, setShowNoDevices] = useState(false);
    const [showError, setShowError] = useState(false);
    const [data, setData] = useState([]);
    const [requestPayloadModalVisibility, setRequestPayloadModalVisibility] = useState(false);

    useEffect(() => {
        setIsMounted(true);

        return () => {
            setIsMounted(false);
        }
    }, []);

    useEffect(() => {
        if (isMounted) {
            fetchData();
        }
    }, [isMounted]);

    const fetchData = () => {
        setIsLoading(true);

        InstallService.gethistoricalinstallchecks(installId).then(response => {
            if (isMounted) {
                setData(response.data);
                setIsLoading(false);
            }
        }).catch(e => {
            console.log(e);
            setIsLoading(false);
        });
    }
    
    const runInstallCheck = async () => {
        setIsLoading(true);
        
        if (registeredDevices === null || registeredDevices === undefined) {
            try {
                let deviceIds = [];
                let request = JSON.parse(requestPayload);
                let configs = request?.CurrentGatewayConfig?.lpxs;

                if (configs != null) {
                    for (let i = 0; i < configs.length; i++) {

                        let assets = configs[i].assets;

                        if (assets != null) {
                            for (let j = 0; j < assets.length; j++) {
                                let cuo = assets[j].cuo;

                                if (cuo != null) {
                                    deviceIds.push(cuo.DeviceID);   
                                }
                            }   
                        }
                    }
                } else {
                    // assume pro app config
                    deviceIds.push(`${macId}-1-1`);
                    deviceIds.push(`${macId}-2-1`);
                }

                if (deviceIds.length > 0) {
                    let deviceStatuses = await DeviceService.getdevicestatuses(deviceIds);

                    if (deviceStatuses !== null && deviceStatuses.data !== null) {
                        if (deviceStatuses.data.length > 0) {
                            await Promise.all(
                                deviceStatuses.data.map(status => MobileInstallCheckService.getinstallcheck(status.assetId))
                            );   
                        } else {
                            setIsLoading(false);
                            setShowNoDevices(true);
                        }
                    }
                } else {
                    setIsLoading(false);
                    setShowNoDevices(true);
                }
            } catch (e) {
                console.log(e);
                setIsLoading(false);
                setShowError(true);
            }
        } else {
            let devices = JSON.parse(registeredDevices);

            await Promise.all(
                devices.map(device => MobileInstallCheckService.getinstallcheck(device.AssetId))
            );   
        }

        if (isMounted) {
            fetchData();
        }
    }

    const getBatteryVoltageColor = (result) => {
        switch (result) {
            case Failure:
                return Red;
            case Warning:
                return Yellow;
            case Passed:
                return Green;
            default:
                return Grey;
        }
    }

    const renderInstallChecks = () => {
        const isT10Gateway = data.find(i => i.gatewayType === 'T10');

        let tableHeadings = isT10Gateway ?
            <tr className={"no-hover device-table-row"}>
                <th className={"pad-1-1-1-10"}>SOURCE</th>
                <th>NICKNAME</th>
                <th>MAC</th>
                <th>STATUS</th>
                <th>TEMP</th>
                <th>BATTERY</th>
                <th>WIFI</th>
                <th>SENSOR HUB</th>
                <th>BLOWER CURRENT</th>
                <th>INDUCER CURRENT</th>
                <th>MAIN CURRENT</th>
                <th>TEST RESULT</th>
                <th>DATE TIME</th>
            </tr> :
            <tr className={"no-hover device-table-row"}>
                <th className={"pad-1-1-1-10"}>SOURCE</th>
                <th>NICKNAME</th>
                <th>MAC</th>
                <th>STATUS</th>
                <th>TEMP</th>
                <th>BATTERY</th>
                <th>WIFI</th>
                <th>SENSOR HUB</th>
                <th>MAIN CURRENT</th>
                <th>TEST RESULT</th>
                <th>DATE TIME</th>
            </tr>

        let tableContent = isT10Gateway ?
                <tbody>
                {data.map((installCheck, index) =>
                    [<tr className={"no-hover device-table-row"} key={`${installCheck.id}-tableRow`} data={index} value={index}>
                        <td className={"border-left pad-1-1-1-10"}>{installCheck.source}</td>
                        <td>{installCheck.nickname}</td>
                        <td>{installCheck.deviceId}</td>
                        <td>{installCheck.deviceStatus}</td>
                        <td>
                            <DevTemp devTemp={installCheck.deviceTemperature}/>
                        </td>
                        <td>
                            <Battery percent={(installCheck.batteryVoltage / 4.5) * 100} color={getBatteryVoltageColor(installCheck.batteryVoltageResult)} outlined={true} className={"battery-icon"}/>
                        </td>
                        <td>
                            <WiFiSymbol wifiStrength={installCheck.wiFiStrength}/>
                        </td>
                        <td>
                            <SignalSymbol sigStrength={installCheck.subGRssiStrength} isT10={isT10Gateway}/>
                        </td>
                        <td>
                            {installCheck.hasMultiCTData ? installCheck.blowerCurrent === null ? 0 + ' A' : installCheck.blowerCurrent + ' A' : 'N/A'}
                            {installCheck.hasMultiCTData ? <CurrentSymbol averageCurrent={installCheck.blowerCurrent} result={installCheck.blowerCurrentResult}/> : null}
                        </td>
                        <td>
                            {installCheck.hasMultiCTData ? installCheck.inducerCurrent === null ? 0 + ' A' : installCheck.inducerCurrent + ' A' : 'N/A'}
                            {installCheck.hasMultiCTData ? <CurrentSymbol averageCurrent={installCheck.inducerCurrent} result={installCheck.inducerCurrentResult}/> : null}
                        </td>
                        <td>
                            {installCheck.mainCurrent === null ? 0 : installCheck.mainCurrent} A
                            <CurrentSymbol averageCurrent={installCheck.mainCurrent} result={installCheck.mainCurrentResult}/>
                        </td>
                        <td>
                            <TestResultSymbol result={installCheck.testResult}/>
                        </td>
                        <td className={"border-right"}>
                            {moment.utc(installCheck.createdDateTime).local().format("M/D/YYYY [at] h:mm A")}
                        </td>
                    </tr>
                    ])}
                </tbody> :
                <tbody>
                {data.map((installCheck, index) =>
                    [<tr className={"no-hover device-table-row"} key={`${installCheck.id}-tableRow`} data={index} value={index}>
                        <td className={"border-left pad-1-1-1-10"}>{installCheck.source}</td>
                        <td>{installCheck.nickname}</td>
                        <td>{installCheck.deviceId}</td>
                        <td>{installCheck.deviceStatus}</td>
                        <td>
                            <DevTemp devTemp={installCheck.deviceTemperature}/>
                        </td>
                        <td>
                            <Battery percent={(installCheck.batteryVoltage / 4.5) * 100} color={getBatteryVoltageColor(installCheck.batteryVoltageResult)} outlined={true} className={"battery-icon"}/>
                        </td>
                        <td>
                            <WiFiSymbol wifiStrength={installCheck.wiFiStrength}/>
                        </td>
                        <td>
                            <SignalSymbol sigStrength={installCheck.subGRssiStrength} isT10={isT10Gateway}/>
                        </td>
                        <td>
                            {installCheck.mainCurrent === null ? 0 : installCheck.mainCurrent} A
                            <CurrentSymbol averageCurrent={installCheck.mainCurrent} result={installCheck.mainCurrentResult}/>
                        </td>
                        <td>
                            <TestResultSymbol result={installCheck.testResult}/>
                        </td>
                        <td className={"border-right"}>
                            {moment.utc(installCheck.createdDateTime).local().format("M/D/YYYY [at] h:mm A")}
                        </td>
                    </tr>
                    ])}
                </tbody>

        return (
            <div>
                {data?.length > 0 && !isLoading ?
                    <table className={"table-hover width-100p margin-bottom-0 font-size-15"}>
                        <thead>
                        {tableHeadings}
                        </thead>
                        {tableContent}
                    </table> : null}
            </div>
        );
    }
    
    const renderButtons = () => {
        let requestPayloadBtn = roles.includes("ADMIN") ?
            <button className="secondary-btn btn-small width-175"
                    type="button"
                    onClick={showRequestPayloadModal}>
                View Request Payload
            </button>
            : null;

        return <div className="row no-gutters alerts-tab-wrapper margin-10-20-0 pad-10-0">
            <div className="col pad-right-30 gap-5 display-flex">
                <button className="primary-btn btn-small width-175"
                        disabled={isLoading}
                        type="button"
                        onClick={runInstallCheck}>
                    Run Install Check
                </button>
                {requestPayloadBtn}
            </div>
        </div>;
    }

    const showRequestPayloadModal = () => {
        setRequestPayloadModalVisibility(!requestPayloadModalVisibility);
        
        if (requestPayloadModalVisibility) {
            ModalView('RequestPayload');
        }
    }
    
    const hideNoDevicesPopUp = () => {
        setShowNoDevices(false);
    }

    const hideErrorPopUp = () => {
        setShowError(false);
    }

    return (
        <div>
            <p className={"margin-bottom-10"}><span className={"semibold"}>Historical Installation Checks:</span> { isLoading ? <div className={"dot-flashing margin-left-25 margin-top-10"}></div> : data?.length > 0 ? '' : 'None'}</p>
            {renderInstallChecks()}
            {renderButtons()}
            <RequestPayloadModal show={requestPayloadModalVisibility} requestPayload={requestPayload} onClick={showRequestPayloadModal} />
            <WarningModal show={showNoDevices} onClick={hideNoDevicesPopUp} message={'No devices were found for this install.'} title={'Devices Not Found'}> </WarningModal>
            <WarningModal show={showError} onClick={hideErrorPopUp} message={'There was a problem running the install check.'} title={'Error'}> </WarningModal>
        </div>
    );
}

export default HistoricalInstallCheck;